// Ctrl K / ⌘ K command palette across words, roots, topics and lessons.

import type { FC, ReactNode } from 'react'

import { useRouter } from 'next/router'
import { useEffect, useMemo, useState } from 'react'
import { BookOpen, FlaskConical, Languages, Loader2, Route, Search } from 'lucide-react'
import { ALL_LESSONS } from '@/prakrit/lib/content/catalog'
import { transliterate } from '@/prakrit/lib/grammar/translit'
import { useWordPanel } from '@/prakrit/components/common/word-panel'
import {
    Command,
    CommandDialog,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList
} from '@/components/ui/command'

interface WordHit {
    form: string
    lemma: string
    hi: string
    en: string
    pos: string
    note?: string
}

interface StaticItem {
    href: string
    hi: string
    en: string
    keywords: string
    group: 'topic' | 'lesson'
}

const TOPICS: StaticItem[] = [
    { href: '/learn', hi: 'पाठ्यक्रम', en: 'Course map', keywords: 'learn course units lessons', group: 'topic' },
    { href: '/grammar/kriya', hi: 'धातुरूप प्रयोगशाला', en: 'Dhātu laboratory', keywords: 'kriya verb dhatu tense kaal conjugation', group: 'topic' },
    { href: '/grammar/kal', hi: 'काल: सामान्य और विकल्प प्रत्यय', en: 'The tenses', keywords: 'kaal kal tense endings pratyay samanya vikalp variant', group: 'topic' },
    { href: '/grammar/kal/vartaman', hi: 'वर्तमान काल', en: 'Present tense', keywords: 'vartaman present tense endings mi mo si', group: 'topic' },
    { href: '/grammar/kal/vidhi', hi: 'विधि एवं आज्ञा', en: 'Wish and command', keywords: 'vidhi ajna agya imperative command mu hi u', group: 'topic' },
    { href: '/grammar/kal/bhoot', hi: 'भूत काल', en: 'Past tense', keywords: 'bhoot bhut past tense ia', group: 'topic' },
    { href: '/grammar/kal/bhavishya', hi: 'भविष्यत् काल', en: 'Future tense', keywords: 'bhavishya future tense himi ssami', group: 'topic' },
    { href: '/grammar/kal/kriyatipatti', hi: 'क्रियातिपत्ति', en: 'Conditional', keywords: 'kriyatipatti conditional jja if then', group: 'topic' },
    { href: '/grammar/purush-vachan', hi: 'पुरुष और वचन', en: 'Person and number grid', keywords: 'purush vachan person number grid pronoun', group: 'topic' },
    { href: '/grammar/vibhakti', hi: 'आठ विभक्तियाँ', en: 'The eight cases', keywords: 'vibhakti case karak nominative accusative', group: 'topic' },
    { href: '/grammar/vakya', hi: 'वाक्य विश्लेषण', en: 'Sentence analyser', keywords: 'vakya sentence analyse parse agreement', group: 'topic' },
    { href: '/grammar/shabdarup', hi: 'शब्दरूप प्रयोगशाला', en: 'Declension laboratory', keywords: 'shabdarup vibhakti noun case declension shabd table', group: 'topic' },
    { href: '/grammar/sarvanam', hi: 'सर्वनाम-रूप', en: 'Pronouns', keywords: 'sarvanam pronoun aham tumam so', group: 'topic' },
    { href: '/grammar/karak', hi: 'कारक / वाक्य विश्लेषण', en: 'Parse a sentence', keywords: 'karak parse sentence roles', group: 'topic' },
    { href: '/grammar/sandhi', hi: 'सन्धि', en: 'Sandhi', keywords: 'sandhi joining', group: 'topic' },
    { href: '/grammar/samas', hi: 'समास', en: 'Compounds', keywords: 'samas compound', group: 'topic' },
    { href: '/grammar/kridanta', hi: 'कृदन्त', en: 'Participles', keywords: 'kridanta participle gerund', group: 'topic' },
    { href: '/grammar/vachya', hi: 'वाच्य एवं प्रेरणार्थक', en: 'Passive and causative', keywords: 'vachya passive causative prerak karmani', group: 'topic' },
    { href: '/grammar/dhvani', hi: 'ध्वनि-परिवर्तन', en: 'Sound changes', keywords: 'dhvani sound change sanskrit', group: 'topic' },
    { href: '/vocabulary', hi: 'शब्दकोश', en: 'Vocabulary', keywords: 'dictionary words shabdkosh', group: 'topic' },
    { href: '/stories', hi: 'कथाएँ', en: 'Stories', keywords: 'stories katha reading dialogue', group: 'topic' },
    { href: '/practice', hi: 'अभ्यास', en: 'Practice and games', keywords: 'practice games abhyas quiz', group: 'topic' },
    { href: '/progress', hi: 'प्रगति', en: 'Progress', keywords: 'progress xp streak mastery', group: 'topic' },
    { href: '/about/content', hi: 'स्रोत और जाँच', en: 'Sources and checks', keywords: 'sources checks review verification', group: 'topic' }
]

const LESSONS: StaticItem[] = ALL_LESSONS.filter(l => l.ready).map(l => ({
    href: `/learn/${l.id}`,
    hi: l.title.hi,
    en: l.title.en,
    keywords: `${l.id} ${l.title.en} ${transliterate(l.title.hi)}`,
    group: 'lesson'
}))

const matches = (item: StaticItem, q: string) => {
    const needle = q.toLowerCase()

    return item.hi.includes(q) || item.en.toLowerCase().includes(needle) || item.keywords.toLowerCase().includes(needle)
}

export const CommandSearch: FC<{ open: boolean, onOpenChange: (open: boolean) => void }> = ({ open, onOpenChange }): ReactNode => {
    const router = useRouter()
    const { open: openWord } = useWordPanel()
    const [q, setQ] = useState('')
    const [words, setWords] = useState<WordHit[]>([])
    const [loading, setLoading] = useState(false)

    useEffect(() => {
        if (!open) setQ('')
    }, [open])

    useEffect(() => {
        const term = q.trim()

        if (term.length < 1) {
            setWords([])

            return
        }

        setLoading(true)

        const controller = new AbortController()
        const timer = setTimeout(() => {
            fetch(`/api/search?q=${encodeURIComponent(term)}`, { signal: controller.signal })
                .then(res => (res.ok ? res.json() : { words: [] }))
                .then((data: { words?: WordHit[] }) => setWords(data.words ?? []))
                .catch(() => undefined)
                .finally(() => setLoading(false))
        }, 160)

        return () => {
            clearTimeout(timer)
            controller.abort()
        }
    }, [q])

    const term = q.trim()
    const topics = useMemo(() => (term ? TOPICS.filter(t => matches(t, term)) : TOPICS.slice(0, 6)), [term])
    const lessons = useMemo(() => (term ? LESSONS.filter(l => matches(l, term)).slice(0, 8) : LESSONS.slice(0, 4)), [term])

    const go = (href: string) => {
        onOpenChange(false)
        router.push(href)
    }

    return (
        <CommandDialog open={open} onOpenChange={onOpenChange} title='खोजें · Search' description='Search words, roots, topics and lessons' className='sm:max-w-xl'>
            <Command shouldFilter={false}>
                <CommandInput
                    value={q}
                    onValueChange={setQ}
                    placeholder='पढामि, पुरिस, भविष्यत्, lesson…'
                    aria-label='Search words, roots, topics and lessons'
                    className='font-hindi'
                />
                <CommandList className='max-h-[60vh]'>
                    {!loading && term && words.length === 0 && topics.length === 0 && lessons.length === 0 && (
                        <CommandEmpty>
                            <span lang='hi' className='hindi'>कुछ नहीं मिला</span> · <span lang='en'>Nothing found</span>
                        </CommandEmpty>
                    )}
                    {loading && (
                        <div className='flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground'>
                            <Loader2 className='size-4 animate-spin' /> <span lang='hi' className='hindi'>खोज रहे हैं…</span>
                        </div>
                    )}
                    {words.length > 0 && (
                        <CommandGroup heading='शब्द और रूप · Words and forms'>
                            {words.map(w => (
                                <CommandItem
                                    key={`${w.form}-${w.lemma}-${w.pos}-${w.note ?? ''}`}
                                    value={`${w.form}-${w.lemma}-${w.pos}-${w.note ?? ''}`}
                                    onSelect={() => {
                                        onOpenChange(false)
                                        openWord(w.form)
                                    }}
                                >
                                    <Languages className='size-4 text-muted-foreground' />
                                    <span lang='pra' className='font-prakrit text-base font-semibold text-prakrit'>{w.form}</span>
                                    <span className='truncate text-sm text-muted-foreground'>
                                        <span lang='hi' className='hindi'>{w.hi}</span> · <span lang='en'>{w.en}</span>
                                        {w.note && <span lang='hi' className='hindi'> — {w.note}</span>}
                                    </span>
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    )}
                    {lessons.length > 0 && (
                        <CommandGroup heading='पाठ · Lessons'>
                            {lessons.map(l => (
                                <CommandItem key={l.href} value={l.href} onSelect={() => go(l.href)}>
                                    <BookOpen className='size-4 text-muted-foreground' />
                                    <span lang='hi' className='hindi'>{l.hi}</span>
                                    <span lang='en' className='text-sm text-muted-foreground'>{l.en}</span>
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    )}
                    {topics.length > 0 && (
                        <CommandGroup heading='विषय · Topics'>
                            {topics.map(t => (
                                <CommandItem key={t.href} value={t.href} onSelect={() => go(t.href)}>
                                    {t.href.startsWith('/grammar') ? <FlaskConical className='size-4 text-muted-foreground' /> : <Route className='size-4 text-muted-foreground' />}
                                    <span lang='hi' className='hindi'>{t.hi}</span>
                                    <span lang='en' className='text-sm text-muted-foreground'>{t.en}</span>
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    )}
                </CommandList>
            </Command>
        </CommandDialog>
    )
}

export const SearchButton: FC<{ onClick: () => void }> = ({ onClick }): ReactNode => (
    <button
        type='button'
        onClick={onClick}
        aria-label='Search (Ctrl K)'
        className='inline-flex h-10 items-center gap-2 rounded-full border border-border bg-card px-3 text-sm text-muted-foreground hover:border-saffron/50 hover:text-foreground'
    >
        <Search className='size-4' />
        <span lang='hi' className='hindi hidden sm:inline'>खोजें</span>
        <kbd className='hidden rounded border border-border bg-secondary px-1.5 font-sans text-[0.6875rem] font-medium xl:inline'>Ctrl K</kbd>
    </button>
)
